import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { createSession, getActiveSession, sendSessionInvite, sendSessionSMS, checkSmsConsent } from '../services/api';
import { useAuth } from '../context/AuthContext';
import SessionHistory from './SessionHistory';

function HomePage() {
  const [activeTab, setActiveTab] = useState('create');
  const [orderId, setOrderId] = useState(localStorage.getItem('svi_last_orderId') || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [homeownerEmail, setHomeownerEmail] = useState('');
  const [emailStatus, setEmailStatus] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [homeownerPhone, setHomeownerPhone] = useState('');
  const [smsStatus, setSmsStatus] = useState('');
  const [sendingCustomSMS, setSendingCustomSMS] = useState(false);
  const [sendingAccessCodeSMS, setSendingAccessCodeSMS] = useState(false);
  const [lastSmsType, setLastSmsType] = useState(null); // 'custom' or 'accessCode'
  const [contactMethod, setContactMethod] = useState('text'); // 'text' or 'email'
  const [isEditingMessage, setIsEditingMessage] = useState(false);
  const [isEditingEmailMessage, setIsEditingEmailMessage] = useState(false);
  const [smsConsentGiven, setSmsConsentGiven] = useState(false);
  const [phoneNeedsConsent, setPhoneNeedsConsent] = useState(false);
  const [checkingConsent, setCheckingConsent] = useState(false);
  const [consentAlreadyOnFile, setConsentAlreadyOnFile] = useState(false);
  // Default message templates
  const DEFAULT_SMS_TEMPLATE = `Hello! 
 
My name is {appraiser_name}.
 
I am confirming the virtual final inspection of your property today, for your loan with {lender}. 
 
Please use the following link to access the inspection app: 
{link} 
 
I will be sending a separate text with an access code that you will need to enter. 
 
Please contact me with any questions at: {appraiser_email}
 
Best Regards, 
{appraiser_name}`;

  const DEFAULT_EMAIL_TEMPLATE = `Hello! 
 
My name is {appraiser_name}.
 
I am confirming the virtual final inspection of your property today, for your loan with {lender}. 
 
Please use the following link to access the inspection app: 
{link} 
 
I will be sending a separate text with an access code that you will need to enter. 
 
Please contact me with any questions at: {appraiser_email}
 
Best Regards, 
{appraiser_name}`;

  const [customMessage, setCustomMessage] = useState(
    localStorage.getItem('svi_sms_template') || DEFAULT_SMS_TEMPLATE
  );
  const [customEmailMessage, setCustomEmailMessage] = useState(
    localStorage.getItem('svi_email_template') || DEFAULT_EMAIL_TEMPLATE
  );
  const [customAccessCodeMessage, setCustomAccessCodeMessage] = useState(
    localStorage.getItem('svi_access_code_template') || 
    `Your Remote Inspection access code is: {access_code}

Enter this code to join your inspection session.`
  );
  const [isEditingAccessCodeMessage, setIsEditingAccessCodeMessage] = useState(false);
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  // Helper function to capitalize first letter of a string
  const capitalizeFirstLetter = (str) => {
    if (!str) return str;
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  // Debounced function to check SMS consent status
  const checkPhoneConsent = useCallback(async (phoneNumber) => {
    if (!phoneNumber || phoneNumber.trim().length < 10) {
      setPhoneNeedsConsent(false);
      setConsentAlreadyOnFile(false);
      return;
    }

    setCheckingConsent(true);
    try {
      const cleanedPhone = formatPhoneForDisplay(phoneNumber);
      const formattedPhone = cleanedPhone.length === 10 ? `1${cleanedPhone}` : cleanedPhone;
      
      const response = await checkSmsConsent(formattedPhone);
      
      if (response.hasConsent) {
        setPhoneNeedsConsent(false);
        setSmsConsentGiven(true); // Auto-set consent if already given
        setConsentAlreadyOnFile(true);
      } else {
        setPhoneNeedsConsent(true);
        setSmsConsentGiven(false);
        setConsentAlreadyOnFile(false);
      }
    } catch (error) {
      console.error('Error checking consent:', error);
      // On error, assume needs consent
      setPhoneNeedsConsent(true);
      setSmsConsentGiven(false);
      setConsentAlreadyOnFile(false);
    } finally {
      setCheckingConsent(false);
    }
  }, []);

  // Debounce phone number input
  useEffect(() => {
    const timer = setTimeout(() => {
      if (homeownerPhone) {
        checkPhoneConsent(homeownerPhone);
      } else {
        setPhoneNeedsConsent(false);
        setSmsConsentGiven(false);
        setConsentAlreadyOnFile(false);
      }
    }, 500); // Wait 500ms after user stops typing

    return () => clearTimeout(timer);
  }, [homeownerPhone, checkPhoneConsent]);

  // Load active session on mount
  useEffect(() => {
    const loadActiveSession = async () => {
      try {
        const data = await getActiveSession();
        if (data && data.id && !data.error) {
          // Set the order ID and result to display the active session
          if (data.orderId) {
            setOrderId(data.orderId);
          }
          // Populate homeowner email if available
          if (data.homeownerEmail) {
            setHomeownerEmail(data.homeownerEmail);
          }
          setResult(data);
        }
      } catch (err) {
        console.log('No active session or error loading:', err.message);
      }
    };
    
    loadActiveSession();
  }, []);

  const handleCreateSession = async () => {
    if (!orderId.trim()) {
      alert('Please enter an Order ID');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await createSession(orderId.trim());
      
      // Check if response contains an error
      if (response.error) {
        setError(response.error === 'Invalid Order' ? 'Invalid Order' : response.error);
        setResult(null);
        return;
      }
      
      setResult(response);
      
      // Populate homeowner email if available
      if (response.homeownerEmail) {
        setHomeownerEmail(response.homeownerEmail);
      }
      
      // Remember for next time
      localStorage.setItem('svi_last_orderId', orderId.trim());
    } catch (err) {
      // Check if error response contains "Invalid Order"
      const errorMessage = err.response?.data?.error || err.message;
      if (errorMessage === 'Invalid Order') {
        setError('Invalid Order');
      } else {
        setError(`Couldn't create session: ${errorMessage}`);
      }
      console.error('Create session failed:', err);
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = async () => {
    await navigator.clipboard.writeText(result.homeownerUrl);
    alert('Homeowner link copied!');
  };

  const handleOpenAppraiserConsole = () => {
    window.open(result.appraiserUrl, '_blank');
  };

  const handleSendEmail = async () => {
    if (!homeownerEmail.trim()) {
      setEmailStatus('Please enter an email address');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(homeownerEmail.trim())) {
      setEmailStatus('Please enter a valid email address');
      return;
    }

    setSendingEmail(true);
    setEmailStatus('');

    try {
      const response = await sendSessionInvite(result.id, homeownerEmail.trim(), customEmailMessage);
      if (response.success) {
        setEmailStatus('✓ Email sent successfully!');
      } else {
        setEmailStatus(`Error: ${response.error || 'Failed to send email'}`);
      }
    } catch (err) {
      setEmailStatus(`Error: ${err.response?.data?.error || err.message || 'Failed to send email'}`);
      console.error('Send email failed:', err);
    } finally {
      setSendingEmail(false);
    }
  };

  const formatPhoneForDisplay = (phone) => {
    // Remove all non-numeric characters
    const cleaned = phone.replace(/\D/g, '');
    return cleaned;
  };

  const handleSendCustomSMS = async () => {
    if (!homeownerPhone.trim()) {
      setSmsStatus('Please enter a phone number');
      return;
    }

    if (!smsConsentGiven) {
      setSmsStatus('Please confirm that you have obtained verbal consent before sending SMS');
      return;
    }

    setSendingCustomSMS(true);
    setSmsStatus('');
    setLastSmsType('custom');

    try {
      // Format phone number with country code 1
      const cleanedPhone = formatPhoneForDisplay(homeownerPhone);
      const formattedPhone = cleanedPhone.length === 10 ? `1${cleanedPhone}` : cleanedPhone;
      
      const response = await sendSessionSMS(result.id, formattedPhone, 'custom', customMessage, smsConsentGiven);
      if (response.success) {
        setSmsStatus('✓ Link sent successfully via SMS');
      } else {
        setSmsStatus(`Error: ${response.error || 'Failed to send SMS'}`);
      }
    } catch (err) {
      setSmsStatus(`Error: ${err.response?.data?.error || err.message || 'Failed to send SMS'}`);
      console.error('Send SMS failed:', err);
    } finally {
      setSendingCustomSMS(false);
    }
  };

  const handleSendAccessCodeSMS = async () => {
    if (!homeownerPhone.trim()) {
      setSmsStatus('Please enter a phone number');
      return;
    }

    if (!smsConsentGiven) {
      setSmsStatus('Please confirm that you have obtained verbal consent before sending SMS');
      return;
    }

    setSendingAccessCodeSMS(true);
    setSmsStatus('');
    setLastSmsType('accessCode');

    try {
      // Format phone number with country code 1
      const cleanedPhone = formatPhoneForDisplay(homeownerPhone);
      const formattedPhone = cleanedPhone.length === 10 ? `1${cleanedPhone}` : cleanedPhone;
      
      const response = await sendSessionSMS(result.id, formattedPhone, 'accessCode', customAccessCodeMessage, smsConsentGiven);
      if (response.success) {
        setSmsStatus('✓ Access code sent successfully via SMS');
      } else {
        setSmsStatus(`Error: ${response.error || 'Failed to send access code'}`);
      }
    } catch (err) {
      setSmsStatus(`Error: ${err.response?.data?.error || err.message || 'Failed to send access code'}`);
      console.error('Send access code SMS failed:', err);
    } finally {
      setSendingAccessCodeSMS(false);
    }
  };

  const handleSaveMessageTemplate = () => {
    localStorage.setItem('svi_sms_template', customMessage);
    setIsEditingMessage(false);
  };

  const handleResetMessageTemplate = () => {
    if (window.confirm('Are you sure you want to reset the SMS message to default?')) {
      setCustomMessage(DEFAULT_SMS_TEMPLATE);
      localStorage.setItem('svi_sms_template', DEFAULT_SMS_TEMPLATE);
      setIsEditingMessage(false);
    }
  };

  const handleSaveEmailTemplate = () => {
    localStorage.setItem('svi_email_template', customEmailMessage);
    setIsEditingEmailMessage(false);
  };

  const handleResetEmailTemplate = () => {
    if (window.confirm('Are you sure you want to reset the email message to default?')) {
      setCustomEmailMessage(DEFAULT_EMAIL_TEMPLATE);
      localStorage.setItem('svi_email_template', DEFAULT_EMAIL_TEMPLATE);
      setIsEditingEmailMessage(false);
    }
  };

  const handleSaveAccessCodeTemplate = () => {
    localStorage.setItem('svi_access_code_template', customAccessCodeMessage);
    setIsEditingAccessCodeMessage(false);
  };

  const getMessagePreview = () => {
    if (!result) return customMessage;
    
    const appraiserName = user?.firstName && user?.lastName 
      ? `${capitalizeFirstLetter(user.firstName)} ${capitalizeFirstLetter(user.lastName)}` 
      : user?.email || 'Your Name';
    const appraiserEmail = user?.email || 'your@email.com';
    const lender = result.lender || '[LENDER NAME]';
    
    return customMessage
      .replace(/{appraiser_name}/g, appraiserName)
      .replace(/{appraiser_email}/g, appraiserEmail)
      .replace(/{lender}/g, lender)
      .replace(/{link}/g, result.homeownerUrl);
  };

  const getEmailPreview = () => {
    if (!result) return customEmailMessage;
    
    const appraiserName = user?.firstName && user?.lastName 
      ? `${capitalizeFirstLetter(user.firstName)} ${capitalizeFirstLetter(user.lastName)}` 
      : user?.email || 'Your Name';
    const appraiserEmail = user?.email || 'your@email.com';
    const orderId = result.orderId || 'N/A';
    const lender = result.lender || '[LENDER NAME]';
    
    return customEmailMessage
      .replace(/{appraiser_name}/g, appraiserName)
      .replace(/{appraiser_email}/g, appraiserEmail)
      .replace(/{order_id}/g, orderId)
      .replace(/{lender}/g, lender)
      .replace(/{link}/g, result.homeownerUrl);
  };

  const getAccessCodePreview = () => {
    if (!result) return customAccessCodeMessage;
    
    return customAccessCodeMessage
      .replace(/{access_code}/g, result.accessCode);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="page">
      {/* Header */}
      <header>
        <img className="logo" src="/logo.png" alt="Stewart Valuation Intelligence" />
        <h1>Remote Inspection</h1>
      </header>

      {/* Main Content */}
      <main className="main">
        <div className="card" style={{width: '100%', maxWidth: '1000px'}}>
          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px'}}>
            <div>
              <h2 style={{margin: 0}}>Inspection Dashboard</h2>
              {user && (
                <p className="hint" style={{margin: '4px 0 0 0'}}>
                  Logged in as: <strong>{user.email}</strong>
                  {user.firstName && user.lastName && ` (${capitalizeFirstLetter(user.firstName)} ${capitalizeFirstLetter(user.lastName)})`}
                </p>
              )}
            </div>
            <button 
              type="button" 
              onClick={handleLogout}
              style={{
                background: 'transparent',
                border: '1px solid #cbd5e1',
                color: '#475569',
                padding: '8px 16px',
                fontSize: '14px'
              }}
            >
              Logout
            </button>
          </div>

          {/* Tab Navigation */}
          <div style={{ 
            display: 'flex', 
            gap: '8px', 
            borderBottom: '2px solid #e5e7eb',
            marginBottom: '20px'
          }}>
            <button
              onClick={() => setActiveTab('create')}
              style={{
                padding: '12px 24px',
                border: 'none',
                background: 'none',
                borderBottom: activeTab === 'create' ? '2px solid #0066cc' : '2px solid transparent',
                color: activeTab === 'create' ? '#0066cc' : '#6b7280',
                fontWeight: activeTab === 'create' ? '600' : '400',
                cursor: 'pointer',
                fontSize: '16px',
                marginBottom: '-2px',
                transition: 'all 0.2s'
              }}
            >
              Create Session
            </button>
            <button
              onClick={() => setActiveTab('history')}
              style={{
                padding: '12px 24px',
                border: 'none',
                background: 'none',
                borderBottom: activeTab === 'history' ? '2px solid #0066cc' : '2px solid transparent',
                color: activeTab === 'history' ? '#0066cc' : '#6b7280',
                fontWeight: activeTab === 'history' ? '600' : '400',
                cursor: 'pointer',
                fontSize: '16px',
                marginBottom: '-2px',
                transition: 'all 0.2s'
              }}
            >
              Session History
            </button>
          </div>

          {activeTab === 'create' ? (
            <>
          <p>Enter the Order ID, then create a unique link for the homeowner and open the inspection console.</p>

          {/* Order ID */}
          <div className="section">
            <label htmlFor="orderId">Order ID</label>
            <input
              id="orderId"
              type="text"
              placeholder="e.g., 12345678"
              value={orderId}
              onChange={(e) => setOrderId(e.target.value)}
              required
              autoComplete="off"
              spellCheck="false"
              maxLength="64"
            />
          </div>

          {/* Create Session */}
          <div className="section">
            <button 
              id="create" 
              type="button" 
              onClick={handleCreateSession}
              disabled={loading}
            >
              {loading ? 'Creating...' : 'Create Session'}
            </button>
          </div>

          {/* Error */}
          {error && (
            <p id="err" className="hint" style={{color: '#b91c1c'}}>
              {error}
            </p>
          )}

          {/* Result Area */}
          {result && (
            <div id="result">
              {/* Homeowner Link Section */}
              <div className="section" style={{padding: '20px', borderRadius: '12px', border: '1px solid #e5e7eb', background: '#f9fafb'}}>
                
                <div style={{marginBottom: '16px'}}>
                  <label htmlFor="homeLink" style={{display: 'block', marginBottom: '6px', fontSize: '14px', color: '#6b7280'}}>
                    Homeowner Link
                  </label>
                  <div className="row">
                    <input 
                      id="homeLink" 
                      type="text" 
                      value={result.homeownerUrl} 
                      readOnly 
                      style={{fontSize: '14px'}}
                    />
                    <button id="copy" type="button" onClick={handleCopyLink}>
                      Copy
                    </button>
                  </div>
                </div>

                <div style={{marginBottom: '16px'}}>
                  <label style={{display: 'block', marginBottom: '6px', fontSize: '14px', color: '#6b7280'}}>
                    Phone Number
                  </label>
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px'
                  }}>
                    <input 
                      type="tel"
                      placeholder="1234567890 or 123-456-7890"
                      value={homeownerPhone}
                      onChange={(e) => {
                        setHomeownerPhone(e.target.value);
                        setSmsStatus('');
                      }}
                      style={{fontSize: '14px', width: '100%'}}
                    />
                    
                    {/* SMS Consent Checkbox - shown when phone number needs consent */}
                    {phoneNeedsConsent && homeownerPhone.trim() && (
                      <div style={{
                        padding: '12px',
                        background: '#fef3c7',
                        border: '1px solid #fbbf24',
                        borderRadius: '6px'
                      }}>
                        <label style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          cursor: 'pointer',
                          fontSize: '13px',
                          fontWeight: '500',
                          color: '#92400e'
                        }}>
                          <input
                            type="checkbox"
                            checked={smsConsentGiven}
                            onChange={(e) => {
                              setSmsConsentGiven(e.target.checked);
                              setSmsStatus('');
                            }}
                            style={{
                              marginRight: '8px',
                              marginTop: '2px',
                              cursor: 'pointer',
                              width: '16px',
                              height: '16px',
                              flexShrink: 0
                            }}
                          />
                          <span>
                            I confirm that I have obtained consent from the homeowner to send them text messages.
                          </span>
                        </label>
                      </div>
                    )}
                    
                    {/* Message shown when consent is already on file */}
                    {consentAlreadyOnFile && homeownerPhone.trim() && !phoneNeedsConsent && (
                      <div style={{
                        padding: '12px',
                        background: '#d1fae5',
                        border: '1px solid #10b981',
                        borderRadius: '6px'
                      }}>
                        <p style={{
                          margin: 0,
                          fontSize: '13px',
                          fontWeight: '500',
                          color: '#065f46'
                        }}>
                          Consent to send homeowner text messages is already on file with SVI
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                <div style={{marginBottom: '16px'}}>
                  <label style={{display: 'block', marginBottom: '6px', fontSize: '14px', color: '#6b7280'}}>
                    Email Address
                  </label>
                    <input 
                      type="email"
                      placeholder="homeowner@example.com"
                      value={homeownerEmail}
                      onChange={(e) => {
                        setHomeownerEmail(e.target.value);
                        setEmailStatus('');
                      }}
                    style={{fontSize: '14px', width: '100%'}}
                  />
                </div>

                {/* Contact Method Selection */}
                <div style={{marginBottom: '16px'}}>
                  <label style={{display: 'block', marginBottom: '8px', fontSize: '14px', color: '#6b7280', fontWeight: '600'}}>
                    Send Instructions Via
                  </label>
                  <div style={{ 
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px',
                    padding: '12px',
                    background: 'white',
                    borderRadius: '6px',
                    border: '1px solid #e5e7eb'
                  }}>
                    <label style={{
                      display: 'flex',
                      alignItems: 'center',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '500',
                      color: '#374151'
                    }}>
                      <input
                        type="radio"
                        name="contactMethod"
                        value="text"
                        checked={contactMethod === 'text'}
                        onChange={() => {
                          setContactMethod('text');
                          setSmsStatus('');
                          setEmailStatus('');
                        }}
                        style={{
                          marginRight: '8px',
                          cursor: 'pointer',
                          width: '16px',
                          height: '16px'
                        }}
                      />
                      📱 Text Message
                    </label>
                    <label style={{
                      display: 'flex',
                      alignItems: 'center',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '500',
                      color: '#374151'
                    }}>
                      <input
                        type="radio"
                        name="contactMethod"
                        value="email"
                        checked={contactMethod === 'email'}
                        onChange={() => {
                          setContactMethod('email');
                          setSmsStatus('');
                          setEmailStatus('');
                        }}
                        style={{
                          marginRight: '8px',
                          cursor: 'pointer',
                          width: '16px',
                          height: '16px'
                        }}
                      />
                      📧 Email
                    </label>
                  </div>
                </div>

                {/* Text/SMS Tab */}
                {contactMethod === 'text' && (
                  <>
                    <div style={{marginBottom: '16px'}}>
                      <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px'}}>
                        <label style={{fontSize: '14px', color: '#6b7280', fontWeight: '600'}}>
                          {isEditingMessage ? 'Message Template' : 'Message Preview'}
                        </label>
                        <div style={{display: 'flex', gap: '8px'}}>
                          {isEditingMessage && (
                            <button 
                              type="button"
                              onClick={handleResetMessageTemplate}
                              style={{
                                background: 'transparent',
                                border: '1px solid #dc2626',
                                color: '#dc2626',
                                padding: '4px 12px',
                                fontSize: '13px',
                                cursor: 'pointer',
                                borderRadius: '6px'
                              }}
                            >
                              Reset to Default
                            </button>
                          )}
                          <button 
                            type="button"
                            onClick={() => {
                              if (isEditingMessage) {
                                handleSaveMessageTemplate();
                              } else {
                                setIsEditingMessage(true);
                              }
                            }}
                            style={{
                              background: 'transparent',
                              border: '1px solid #cbd5e1',
                              color: '#475569',
                              padding: '4px 12px',
                              fontSize: '13px',
                              cursor: 'pointer',
                              borderRadius: '6px'
                            }}
                          >
                            {isEditingMessage ? 'Save' : 'Edit'}
                          </button>
                        </div>
                      </div>
                      
                      <div style={{width: '100%'}}>
                        {isEditingMessage ? (
                          <>
                            <textarea
                              value={customMessage}
                              onChange={(e) => setCustomMessage(e.target.value)}
                              rows="5"
                              style={{
                                width: '100%',
                                maxWidth: '100%',
                                minWidth: '100%',
                                fontSize: '13px',
                                fontFamily: 'inherit',
                                padding: '12px',
                                borderRadius: '6px',
                                border: '1px solid #d1d5db',
                                resize: 'vertical',
                                marginBottom: '4px',
                                boxSizing: 'border-box',
                                margin: 0,
                                lineHeight: '1.5',
                                display: 'block'
                              }}
                            />
                            <p style={{fontSize: '12px', color: '#6b7280', margin: 0}}>
                              Use <code>&#123;appraiser_name&#125;</code>, <code>&#123;appraiser_email&#125;</code>, <code>&#123;lender&#125;</code>, and <code>&#123;link&#125;</code> as placeholders
                            </p>
                          </>
                        ) : (
                          <div style={{
                            fontSize: '13px',
                            color: '#374151',
                            whiteSpace: 'pre-wrap',
                            padding: '12px',
                            background: 'white',
                            borderRadius: '6px',
                            border: '1px solid #e5e7eb',
                            lineHeight: '1.5',
                            boxSizing: 'border-box',
                            width: '100%'
                          }}>
                            {getMessagePreview()}
                          </div>
                        )}
                      </div>
                    </div>

                    <button 
                      type="button" 
                      onClick={handleSendCustomSMS}
                      disabled={sendingCustomSMS || !homeownerPhone.trim() || !smsConsentGiven}
                      style={{
                        width: '100%',
                        padding: '10px',
                        fontSize: '14px',
                        fontWeight: '600',
                        background: sendingCustomSMS || !homeownerPhone.trim() || !smsConsentGiven ? undefined : '#9E2339',
                        borderColor: sendingCustomSMS || !homeownerPhone.trim() || !smsConsentGiven ? undefined : '#9E2339',
                        opacity: (!smsConsentGiven || !homeownerPhone.trim()) ? 0.5 : 1
                      }}
                    >
                      {sendingCustomSMS ? 'Sending...' : 'Send Text Message'}
                    </button>

                    {/* SMS Status Message */}
                    {smsStatus && (lastSmsType === 'custom' || smsStatus.includes('Link') || smsStatus.includes('link')) && (
                      <p style={{
                        marginTop: '8px',
                        marginBottom: 0,
                        fontSize: '13px',
                        color: smsStatus.includes('✓') || smsStatus.includes('success') ? '#15803d' : '#dc2626'
                      }}>
                        {smsStatus}
                      </p>
                    )}
                  </>
                )}

                {/* Email Tab */}
                {contactMethod === 'email' && (
                  <>
                    <div style={{marginBottom: '16px'}}>
                      <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px'}}>
                        <label style={{fontSize: '14px', color: '#6b7280', fontWeight: '600'}}>
                          {isEditingEmailMessage ? 'Email Template' : 'Email Preview'}
                        </label>
                        <div style={{display: 'flex', gap: '8px'}}>
                          {isEditingEmailMessage && (
                            <button 
                              type="button"
                              onClick={handleResetEmailTemplate}
                              style={{
                                background: 'transparent',
                                border: '1px solid #dc2626',
                                color: '#dc2626',
                                padding: '4px 12px',
                                fontSize: '13px',
                                cursor: 'pointer',
                                borderRadius: '6px'
                              }}
                            >
                              Reset to Default
                            </button>
                          )}
                          <button 
                            type="button"
                            onClick={() => {
                              if (isEditingEmailMessage) {
                                handleSaveEmailTemplate();
                              } else {
                                setIsEditingEmailMessage(true);
                              }
                            }}
                            style={{
                              background: 'transparent',
                              border: '1px solid #cbd5e1',
                              color: '#475569',
                              padding: '4px 12px',
                              fontSize: '13px',
                              cursor: 'pointer',
                              borderRadius: '6px'
                            }}
                          >
                            {isEditingEmailMessage ? 'Save' : 'Edit'}
                          </button>
                        </div>
                      </div>
                      
                      <div style={{width: '100%'}}>
                        {isEditingEmailMessage ? (
                          <>
                            <textarea
                              value={customEmailMessage}
                              onChange={(e) => setCustomEmailMessage(e.target.value)}
                              rows="12"
                              style={{
                                width: '100%',
                                maxWidth: '100%',
                                minWidth: '100%',
                                fontSize: '13px',
                                fontFamily: 'inherit',
                                padding: '12px',
                                borderRadius: '6px',
                                border: '1px solid #d1d5db',
                                resize: 'vertical',
                                marginBottom: '4px',
                                boxSizing: 'border-box',
                                margin: 0,
                                lineHeight: '1.5',
                                display: 'block'
                              }}
                            />
                            <p style={{fontSize: '12px', color: '#6b7280', margin: 0}}>
                              Use <code>&#123;appraiser_name&#125;</code>, <code>&#123;appraiser_email&#125;</code>, <code>&#123;order_id&#125;</code>, <code>&#123;lender&#125;</code>, and <code>&#123;link&#125;</code> as placeholders
                            </p>
                          </>
                        ) : (
                          <div style={{
                            fontSize: '13px',
                            color: '#374151',
                            whiteSpace: 'pre-wrap',
                            padding: '12px',
                            background: 'white',
                            borderRadius: '6px',
                            border: '1px solid #e5e7eb',
                            lineHeight: '1.5',
                            maxHeight: '300px',
                            overflowY: 'auto',
                            boxSizing: 'border-box',
                            width: '100%'
                          }}>
                            {getEmailPreview()}
                          </div>
                        )}
                      </div>
                    </div>

                    <button 
                      type="button" 
                      onClick={handleSendEmail}
                      disabled={sendingEmail || !homeownerEmail.trim()}
                      style={{
                        width: '100%',
                        padding: '10px',
                        fontSize: '14px',
                        fontWeight: '600',
                        marginBottom: '8px'
                      }}
                    >
                      {sendingEmail ? 'Sending...' : 'Send Email'}
                    </button>

                  {emailStatus && (
                    <p style={{
                        marginTop: 0,
                      marginBottom: 0,
                      fontSize: '13px',
                      color: emailStatus.includes('✓') || emailStatus.includes('success') ? '#15803d' : '#dc2626'
                    }}>
                      {emailStatus}
                    </p>
                  )}
                  </>
                )}

                <div style={{marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #e5e7eb'}}>
                  <label style={{display: 'block', marginBottom: '6px', fontSize: '14px', color: '#6b7280', fontWeight: '600'}}>
                    Access Code (send via text)
                  </label>
                  
                  <div style={{marginBottom: '12px'}}>
                    <div className="row">
                      <input 
                        type="text"
                        value={result.accessCode}
                        readOnly
                        style={{
                          fontSize: '18px',
                          fontWeight: '600',
                          letterSpacing: '4px',
                          fontFamily: 'monospace',
                          textAlign: 'center'
                        }}
                      />
                      <button 
                        type="button" 
                        onClick={() => {
                          navigator.clipboard.writeText(result.accessCode);
                          alert('Access code copied!');
                        }}
                      >
                        Copy
                      </button>
                    </div>
                  </div>

                  <div style={{marginBottom: '12px'}}>
                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px'}}>
                      <label style={{fontSize: '14px', color: '#6b7280', fontWeight: '600'}}>
                        {isEditingAccessCodeMessage ? 'Access Code Message Template' : 'Access Code Message Preview'}
                      </label>
                      <button 
                        type="button"
                        onClick={() => {
                          if (isEditingAccessCodeMessage) {
                            handleSaveAccessCodeTemplate();
                          } else {
                            setIsEditingAccessCodeMessage(true);
                          }
                        }}
                        style={{
                          background: 'transparent',
                          border: '1px solid #cbd5e1',
                          color: '#475569',
                          padding: '4px 12px',
                          fontSize: '13px',
                          cursor: 'pointer',
                          borderRadius: '6px'
                        }}
                      >
                        {isEditingAccessCodeMessage ? 'Save' : 'Edit'}
                      </button>
                    </div>
                    
                    <div style={{width: '100%'}}>
                      {isEditingAccessCodeMessage ? (
                        <>
                          <textarea
                            value={customAccessCodeMessage}
                            onChange={(e) => setCustomAccessCodeMessage(e.target.value)}
                            rows="3"
                            style={{
                              width: '100%',
                              maxWidth: '100%',
                              minWidth: '100%',
                              fontSize: '13px',
                              fontFamily: 'inherit',
                              padding: '12px',
                              borderRadius: '6px',
                              border: '1px solid #d1d5db',
                              resize: 'vertical',
                              marginBottom: '4px',
                              boxSizing: 'border-box',
                              margin: 0,
                              lineHeight: '1.5',
                              display: 'block'
                            }}
                          />
                          <p style={{fontSize: '12px', color: '#6b7280', margin: 0}}>
                            Use <code>&#123;access_code&#125;</code> as placeholder
                          </p>
                        </>
                      ) : (
                        <div style={{
                          fontSize: '13px',
                          color: '#374151',
                          whiteSpace: 'pre-wrap',
                          padding: '12px',
                          background: 'white',
                          borderRadius: '6px',
                          border: '1px solid #e5e7eb',
                          lineHeight: '1.5',
                          boxSizing: 'border-box',
                          width: '100%'
                        }}>
                          {getAccessCodePreview()}
                        </div>
                      )}
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={handleSendAccessCodeSMS}
                    disabled={sendingAccessCodeSMS || !homeownerPhone.trim() || !smsConsentGiven}
                    style={{
                      width: '100%',
                      padding: '10px',
                      fontSize: '14px',
                      fontWeight: '600',
                      background: sendingAccessCodeSMS || !homeownerPhone.trim() || !smsConsentGiven ? undefined : '#9E2339',
                      borderColor: sendingAccessCodeSMS || !homeownerPhone.trim() || !smsConsentGiven ? undefined : '#9E2339',
                      opacity: (!smsConsentGiven || !homeownerPhone.trim()) ? 0.5 : 1
                    }}
                  >
                    {sendingAccessCodeSMS ? 'Sending...' : 'Send Access Code via Text'}
                  </button>

                  {/* SMS Status Message for Access Code */}
                  {smsStatus && (lastSmsType === 'accessCode' || smsStatus.includes('Access code') || smsStatus.includes('access code')) && (
                    <p style={{
                      marginTop: '8px',
                      marginBottom: 0,
                      fontSize: '13px',
                      color: smsStatus.includes('✓') || smsStatus.includes('success') ? '#15803d' : '#dc2626'
                    }}>
                      {smsStatus}
                    </p>
                  )}
                </div>
              </div>

              <div className="section">
                <button type="button" onClick={handleOpenAppraiserConsole}>
                  Open Inspection Console
                </button>
              </div>
            </div>
          )}
            </>
          ) : (
            <SessionHistory />
          )}
        </div>
      </main>
    </div>
  );
}

export default HomePage;
